import React, { FC, useEffect, useState } from 'react';
import styles from './FilePreview.module.scss';
import ButtonWithImage from 'components/ButtonWithImage';
import { DocumentImage, DownloadImage, EmptyDocumentsImage } from 'static/images';
import Button from 'components/Button';
import { useHistory } from 'react-router-dom';
import { StylesProvider } from '@material-ui/core';
import LinearProgress from '@material-ui/core/LinearProgress';
import { DocumentExtension } from 'api/LoanOriginationSystem/DocumentsApi';
import { downloadFile } from 'utils/downloadFile';
import clsx from 'clsx';
import ImagePreview from './ImagePreview';
import { applicationDocumentsApi, loanOriginationSystemEmailsApi } from 'stores/loanOriginationSystem';
import { DownloadResponse } from 'api/APIRest';
import { FileType } from 'pages/LoanOriginationSystem/FilePreview';

interface FilePreviewProps {
  fileType: FileType;
  fileExtension: DocumentExtension;
  filename: string;
  previewLink?: string;
  fileId: string;
}

const GOOGLE_DOCS_EXTENSIONS = [
  DocumentExtension.Ppt,
  DocumentExtension.Pptx,
  DocumentExtension.Txt,
  DocumentExtension.Word,
  DocumentExtension.Doc,
  DocumentExtension.Docx,
  DocumentExtension.Xml,
  DocumentExtension.Xls,
  DocumentExtension.Xlsx,
  DocumentExtension.Csv,
  DocumentExtension.Json,
  DocumentExtension.Zip,
];

const IMAGE_EXTENSIONS = [
  DocumentExtension.Gif,
  DocumentExtension.Jpeg,
  DocumentExtension.Jpg,
  DocumentExtension.Png,
  DocumentExtension.Svg,
];

const isSupportedDocumentExtension = (extension: DocumentExtension) =>
  GOOGLE_DOCS_EXTENSIONS.includes(extension) ||
  IMAGE_EXTENSIONS.includes(extension) ||
  extension === DocumentExtension.Pdf;

const FilePreview: FC<FilePreviewProps> = ({ fileExtension, previewLink, filename, fileId, fileType }) => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [noPreviewAvailable, setNoPreviewAvailable] = useState(!isSupportedDocumentExtension(fileExtension));
  const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    const downloadPdf = async () => {
      try {
        let response: DownloadResponse;

        if (fileType === FileType.EmailAttachment) {
          response = await loanOriginationSystemEmailsApi.downloadSingleAttachment(fileId);
        } else {
          response = await applicationDocumentsApi.download(fileId);
        }

        setPdfObjectUrl(window.URL.createObjectURL(response.file));
      } catch (error) {
        setIsLoading(false);
        setNoPreviewAvailable(true);
      }
    };

    if (fileExtension === DocumentExtension.Pdf) {
      downloadPdf();
    }
  }, [fileExtension, fileId]);

  useEffect(() => {
    setIsLoading(!previewLink || isSupportedDocumentExtension(fileExtension));
  }, [fileExtension, previewLink]);

  const handleLoadSuccess = () => {
    setIsLoading(false);
  };

  const handleLoadFailure = () => {
    setIsLoading(false);
    setNoPreviewAvailable(true);
  };

  const renderFilePreview = () => {
    if (!noPreviewAvailable && GOOGLE_DOCS_EXTENSIONS.includes(fileExtension)) {
      const url = `https://docs.google.com/gview?url=${previewLink}&embedded=true`;

      return (
        <iframe
          title={filename}
          src={url}
          onLoad={handleLoadSuccess}
          onError={handleLoadFailure}
          className={styles.filePreview}
        />
      );
    }

    if (!noPreviewAvailable && IMAGE_EXTENSIONS.includes(fileExtension)) {
      return (
        <ImagePreview
          previewLink={previewLink!}
          filename={filename}
          onLoad={handleLoadSuccess}
          onError={handleLoadFailure}
        />
      );
    }

    if (!noPreviewAvailable && pdfObjectUrl) {
      return (
        <iframe
          title={filename}
          src={pdfObjectUrl}
          className={styles.filePreview}
          onLoad={handleLoadSuccess}
          onError={handleLoadFailure}
        />
      );
    }

    return (
      <>
        <EmptyDocumentsImage />
        <p>No preview is available.</p>
        <Button kind="secondary" className={styles.downloadButton} onClick={handleDownloadFile} disabled={!previewLink}>
          <DownloadImage className={styles.downloadImage} />
          Download
        </Button>
      </>
    );
  };

  const handleGoBack = () => {
    history.goBack();
  };

  const handleDownloadFile = () => {
    downloadFile(previewLink!, filename);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <ButtonWithImage title="Back" kind="back" onClick={handleGoBack} />
        <h6 className={styles.fileName}>{filename}</h6>
        <ButtonWithImage title="Download" kind="download" onClick={handleDownloadFile} disabled={!previewLink} />
      </div>
      {isLoading && (
        <StylesProvider injectFirst>
          <LinearProgress className={styles.progressBar} />
        </StylesProvider>
      )}
      <div className={styles.body}>
        {isLoading && (
          <div className={styles.fileContainer}>
            <DocumentImage />
            <p>Preparing File For Viewing</p>
            {!!previewLink && (
              <ButtonWithImage
                className={styles.downloadLinkBtn}
                title="Download"
                kind="download"
                onClick={handleDownloadFile}
              />
            )}
          </div>
        )}
        <div className={clsx(styles.fileContainer, isLoading && styles.hidden)}>
          {previewLink && renderFilePreview()}
        </div>
      </div>
    </div>
  );
};

export default FilePreview;
